/**
 * Date formatting utilities for consistent date display across the application.
 * These functions bypass JavaScript's Date object to avoid timezone conversion issues.
 */

/**
 * Formats a date string from ISO format (YYYY-MM-DD) to a human-readable format (MMM D, YYYY)
 * This method intentionally avoids using the Date object to prevent timezone conversion issues
 * 
 * @param {string} dateString - Date string in ISO format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS...)
 * @returns {string} Formatted date string (e.g., "Apr 17, 2025")
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Parse the date string to extract year, month, day directly
    // This avoids any timezone conversion issues
    const parts = dateString.split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1], 10);
      const day = parseInt(parts[2], 10);
      
      // Create a date string in "MMM D, YYYY" format
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[month-1]} ${day}, ${year}`;
    }
    
    // Fallback for any other date format
    return dateString.split('T')[0];
  } catch (error) {
    console.error('Error formatting date:', dateString, error);
    return dateString || '';
  }
};

/**
 * Formats a boolean value as "Yes" or "No"
 * 
 * @param {boolean} value - Boolean value to format
 * @returns {string} "Yes", "No", or empty string
 */
export const formatBoolean = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return '';
};

/**
 * Formats a currency value
 * 
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
}; 